import {
  Checkbox,
  Col,
  DatePicker,
  DatePickerProps,
  Divider,
  Flex,
  Form,
  Input,
  InputNumber,
  Row
} from 'antd'

import { TriState } from 'src/api/requests'
import { Text } from 'src/components'
import { isHivIcd10Code } from 'src/features/persons/utils/diagnosis'
import { dayMonthYearFormatWithDot, disabledFutureDate } from 'src/libs'
import { useLocales } from 'src/locales'

import { useHivAnamnesisClassifiers } from '../../hooks/useHivAnamnesisClassifiers'
import { HivClassifierSelect } from '../fields/HivClassifierSelect'
import { useIcd10Code } from '../fields/HivIcd10Select'
import {
  OpportunisticDiseasesField,
  validateOpportunisticDiseases
} from '../fields/OpportunisticDiseasesField'
import { TriStateItem } from '../fields/TriStateItem'
import { HivFormItem } from '../HivFormItem'
import { HivInstitutionSelect } from '../HivInstitutionSelect'

type Path = (string | number)[]

type Props = {
  /** Where section F lives in the form: `['form', 'clinical']`, or `['clinical']` in the yearly update. */
  prefix: Path
  /** The ICD-10-AM element of the diagnosis (20): an HIV diagnosis shows 55.2 whatever 55 is. */
  diagnosisIcd10Id?: string | null
}

// Form.Item passes value / onChange to its direct child, so they are forwarded.
const DateInput = (props: DatePickerProps) => (
  <DatePicker
    className="full-width"
    format={dayMonthYearFormatWithDot}
    disabledDate={disabledFutureDate}
    {...props}
  />
)

/** 52.1 / 52.2: the row (number + label) with its date and quantity, numbered as on the form. */
const ViralLoad = ({
  name,
  number,
  label,
  dateNumber,
  quantityNumber
}: {
  name: Path
  number: string
  label: string
  dateNumber: string
  quantityNumber: string
}) => {
  const { t } = useLocales()
  const form = Form.useFormInstance()
  const undetectable = Form.useWatch([...name, 'undetectable'], form)

  return (
    <Row gutter={[24, 24]}>
      <Col span={24}>
        <Text>{`${number}. ${label}`}</Text>
      </Col>

      <Col xs={24} lg={8}>
        <HivFormItem
          name={[...name, 'date']}
          number={dateNumber}
          label={t('hiv_anamnesis.clinical.date')}
        >
          <DateInput />
        </HivFormItem>
      </Col>

      <Col xs={24} lg={8}>
        <HivFormItem
          name={[...name, 'copiesPerMl']}
          number={quantityNumber}
          label={t('hiv_anamnesis.clinical.copies_per_ml')}
        >
          <InputNumber
            className="full-width"
            min={0}
            disabled={undetectable || undefined}
          />
        </HivFormItem>
      </Col>

      <Col xs={24} lg={8}>
        <HivFormItem
          name={[...name, 'undetectable']}
          valuePropName="checked"
          label=" "
        >
          <Checkbox
            onChange={(e) => {
              if (e.target.checked) {
                form.setFieldValue([...name, 'copiesPerMl'], null)
              }
            }}
          >
            {t('hiv_anamnesis.clinical.undetectable')}
          </Checkbox>
        </HivFormItem>
      </Col>
    </Row>
  )
}

/**
 * A CD4 result. 53.1 / 53.2 are a row with the date, count and percent numbered as on the form
 * (`subNumbers`); the CD4 level of 55.2 has no sub-numbers.
 */
const Cd4 = ({
  name,
  number,
  label,
  subNumbers
}: {
  name: Path
  number?: string
  label: string
  subNumbers?: [date: string, count: string, percent: string]
}) => {
  const { t } = useLocales()

  return (
    <Row gutter={[24, 24]}>
      {subNumbers && (
        <Col span={24}>
          <Text>{`${number}. ${label}`}</Text>
        </Col>
      )}

      <Col xs={24} lg={8}>
        <HivFormItem
          name={[...name, 'date']}
          number={subNumbers ? subNumbers[0] : number}
          label={subNumbers ? t('hiv_anamnesis.clinical.date') : label}
        >
          <DateInput />
        </HivFormItem>
      </Col>

      <Col xs={12} lg={8}>
        <HivFormItem
          name={[...name, 'count']}
          number={subNumbers?.[1]}
          label={t('hiv_anamnesis.clinical.cd4_count')}
        >
          <InputNumber className="full-width" min={0} />
        </HivFormItem>
      </Col>

      <Col xs={12} lg={8}>
        <HivFormItem
          name={[...name, 'percent']}
          number={subNumbers?.[2]}
          label={t('hiv_anamnesis.clinical.cd4_percent')}
        >
          <InputNumber className="full-width" min={0} max={100} />
        </HivFormItem>
      </Col>
    </Row>
  )
}

const TreatedCondition = ({
  name,
  number,
  condition
}: {
  name: Path
  number: string
  condition: 'tuberculosis' | 'hepatitisC' | 'hepatitisB'
}) => {
  const { t } = useLocales()
  const form = Form.useFormInstance()
  const diagnosed = Form.useWatch([...name, 'diagnosed'], form)
  const receivesTreatment = Form.useWatch([...name, 'receivesTreatment'], form)

  return (
    <Row gutter={[24, 24]}>
      <TriStateItem
        name={[...name, 'diagnosed']}
        number={number}
        label={t(`hiv_anamnesis.clinical.${condition}`)}
        span={8}
        yesNoOnly
      />

      {diagnosed === TriState.YES && (
        <TriStateItem
          name={[...name, 'receivesTreatment']}
          number={`${number}.1`}
          label={t('hiv_anamnesis.clinical.receives_treatment')}
          span={8}
          yesNoOnly
        />
      )}

      {diagnosed === TriState.YES && receivesTreatment === TriState.YES && (
        <Col xs={24} lg={8}>
          <HivFormItem
            name={[...name, 'treatmentStartDate']}
            number={`${number}.2`}
            label={t('hiv_anamnesis.clinical.treatment_start_date')}
          >
            <DateInput />
          </HivFormItem>
        </Col>
      )}
    </Row>
  )
}

/**
 * F. Clinical data (fields 51–60.1), also the content of the yearly ECDC update. Optional since 2026-10-15;
 * kept: Da / Nu only for 54, 55, 57–59, an ICD-10 element per disease row (56), 60.1 not before 60.
 */
export const ClinicalFields = ({ prefix, diagnosisIcd10Id }: Props) => {
  const { t } = useLocales()
  const form = Form.useFormInstance()
  const { aidsClinicalCategoryId } = useHivAnamnesisClassifiers()
  const path = (...parts: string[]) => [...prefix, ...parts]

  const acuteSuspected = Form.useWatch(path('acuteInfectionSuspected'), form)
  const otherEvidence = Form.useWatch(path('otherAcuteInfectionEvidence'), form)
  const arvTreatment = Form.useWatch(path('arvTreatment'), form)
  const aidsStage = Form.useWatch(path('aidsStage'), form)
  const aidsCategory = Form.useWatch(path('aidsClinicalCategoryId'), form)
  const diagnosisCode = useIcd10Code(diagnosisIcd10Id)

  // 55.2 is prefilled from the consultations, so it is shown with an HIV diagnosis (20) or once it has a
  // value too, not only when 55 is Da.
  const showAidsCategory =
    aidsStage === TriState.YES ||
    isHivIcd10Code(diagnosisCode) ||
    !!aidsCategory

  return (
    <Flex vertical gap={24}>
      <Row gutter={[24, 24]}>
        <TriStateItem
          name={path('acuteInfectionSuspected')}
          number="51"
          label={t('hiv_anamnesis.clinical.acute_suspected')}
        />
      </Row>

      {acuteSuspected === TriState.YES && (
        <Row gutter={[24, 24]}>
          <TriStateItem
            name={path('acuteRetroviralSyndromeSigns')}
            number="51.1"
            label={t('hiv_anamnesis.clinical.acute_signs')}
          />
          <TriStateItem
            name={path('otherAcuteInfectionEvidence')}
            number="51.2"
            label={t('hiv_anamnesis.clinical.other_evidence')}
          />

          {otherEvidence === TriState.YES && (
            <Col span={24}>
              <HivFormItem
                name={path('otherAcuteInfectionEvidenceDescription')}
                label={t('hiv_anamnesis.clinical.other_evidence_description')}
              >
                <Input.TextArea rows={2} maxLength={1000} />
              </HivFormItem>
            </Col>
          )}
        </Row>
      )}

      <Row gutter={[24, 24]}>
        <Col xs={24} lg={8}>
          <HivFormItem
            name={path('lastNegativeTestDate')}
            number="51.3.1"
            label={t('hiv_anamnesis.clinical.last_negative_date')}
          >
            <DateInput />
          </HivFormItem>
        </Col>

        <Col xs={24} lg={16}>
          <HivFormItem
            name={path('lastNegativeTestInstitutionId')}
            number="51.3.2"
            label={t('hiv_anamnesis.clinical.last_negative_place')}
          >
            <HivInstitutionSelect />
          </HivFormItem>
        </Col>
      </Row>

      <Divider style={{ margin: 0 }} />
      <Text size="xl">{`52. ${t('hiv_anamnesis.clinical.viral_load_title')}`}</Text>
      <Flex vertical gap={24}>
        {/* The form numbers the latest viral load's date and quantity 52.2.2 / 52.2.3. */}
        <ViralLoad
          name={path('firstViralLoad')}
          number="52.1"
          label={t('hiv_anamnesis.clinical.viral_load_first')}
          dateNumber="52.1.1"
          quantityNumber="52.1.2"
        />
        <ViralLoad
          name={path('latestViralLoad')}
          number="52.2"
          label={t('hiv_anamnesis.clinical.viral_load_latest')}
          dateNumber="52.2.2"
          quantityNumber="52.2.3"
        />
      </Flex>

      <Text size="xl">{`53. ${t('hiv_anamnesis.clinical.cd4_title')}`}</Text>
      <Flex vertical gap={24}>
        <Cd4
          name={path('firstCd4')}
          number="53.1"
          label={t('hiv_anamnesis.clinical.cd4_first')}
          subNumbers={['53.1.1', '53.1.2', '53.1.3']}
        />
        <Cd4
          name={path('latestCd4')}
          number="53.2"
          label={t('hiv_anamnesis.clinical.cd4_latest')}
          subNumbers={['53.2.1', '53.2.2', '53.2.3']}
        />
      </Flex>

      <Divider style={{ margin: 0 }} />
      <Row gutter={[24, 24]}>
        <TriStateItem
          name={path('arvTreatment')}
          number="54"
          label={t('hiv_anamnesis.clinical.arv_treatment')}
          span={8}
          yesNoOnly
        />

        {arvTreatment === TriState.YES && (
          <>
            <Col xs={24} lg={8}>
              <HivFormItem
                name={path('arvStartDate')}
                number="54.1"
                label={t('hiv_anamnesis.clinical.arv_start_date')}
              >
                <DateInput />
              </HivFormItem>
            </Col>

            <Col xs={24} lg={8}>
              <HivFormItem
                name={path('arvLatestAdministrationDate')}
                number="54.2"
                label={t('hiv_anamnesis.clinical.arv_latest_date')}
              >
                <DateInput />
              </HivFormItem>
            </Col>
          </>
        )}
      </Row>

      <Row gutter={[24, 24]}>
        <TriStateItem
          name={path('aidsStage')}
          number="55"
          label={t('hiv_anamnesis.clinical.aids_stage')}
          span={8}
          yesNoOnly
        />

        {aidsStage === TriState.YES && (
          <Col xs={24} lg={8}>
            <HivFormItem
              name={path('aidsStageDate')}
              number="55.1"
              label={t('hiv_anamnesis.clinical.aids_stage_date')}
            >
              <DateInput />
            </HivFormItem>
          </Col>
        )}

        {showAidsCategory && (
          <Col xs={24} lg={8}>
            <HivFormItem
              name={path('aidsClinicalCategoryId')}
              number="55.2"
              label={t('hiv_anamnesis.clinical.aids_category')}
            >
              <HivClassifierSelect classifierId={aidsClinicalCategoryId} />
            </HivFormItem>
          </Col>
        )}
      </Row>

      {showAidsCategory && (
        <Cd4
          name={path('aidsCd4')}
          number="55.2"
          label={t('hiv_anamnesis.clinical.aids_cd4')}
        />
      )}

      <Divider style={{ margin: 0 }} />
      <HivFormItem
        name={path('opportunisticDiseases')}
        number="56"
        label={t('hiv_anamnesis.clinical.opportunistic_diseases')}
        rules={[
          {
            validator: validateOpportunisticDiseases(
              t('hiv_anamnesis.clinical.disease_incomplete')
            )
          }
        ]}
      >
        <OpportunisticDiseasesField />
      </HivFormItem>

      <Divider style={{ margin: 0 }} />
      <TreatedCondition
        name={path('tuberculosis')}
        number="57"
        condition="tuberculosis"
      />
      <TreatedCondition
        name={path('hepatitisC')}
        number="58"
        condition="hepatitisC"
      />
      <TreatedCondition
        name={path('hepatitisB')}
        number="59"
        condition="hepatitisB"
      />

      <Divider style={{ margin: 0 }} />
      <Row gutter={[24, 24]}>
        <Col xs={24} lg={8}>
          <HivFormItem
            name={path('firstVisitDate')}
            number="60"
            label={t('hiv_anamnesis.clinical.first_visit')}
          >
            <DateInput />
          </HivFormItem>
        </Col>

        <Col xs={24} lg={8}>
          <HivFormItem
            name={path('lastVisitDate')}
            number="60.1"
            label={t('hiv_anamnesis.clinical.last_visit')}
            dependencies={[path('firstVisitDate')]}
            rules={[
              ({ getFieldValue }) => ({
                validator: (_, value) => {
                  const first = getFieldValue(path('firstVisitDate'))

                  return value && first && value.isBefore(first, 'day')
                    ? Promise.reject(
                        t('hiv_anamnesis.clinical.last_visit_before_first')
                      )
                    : Promise.resolve()
                }
              })
            ]}
          >
            <DateInput />
          </HivFormItem>
        </Col>
      </Row>
    </Flex>
  )
}
